/**
 * DCSE · Quick add to cart (compartido)
 *
 * Antes, esta misma lógica (agregar al carrito por fetch, actualizar la burbuja
 * del carrito y mostrar un aviso tipo "toast") estaba copiada y pegada, casi
 * palabra por palabra, dentro de varias secciones personalizadas:
 * ADD-hero-carrusel, ADD-colecciones-destacadas, ADD-especial-marca,
 * ADD-marcas-destacadas y related-products. Cada copia tenía su propio toast,
 * su propio manejador de errores y pequeñas diferencias que hacían más difícil
 * mantenerlas iguales. Este archivo centraliza ese comportamiento en un solo
 * lugar para que todas las secciones lo reutilicen.
 *
 * Sincronización del carrito: se pide a Shopify exactamente las secciones que
 * el propio componente nativo de Dawn (<cart-drawer> o <cart-notification>)
 * declara necesitar (mismo `getSectionsToRender()` que usa assets/product-form.js
 * para el add-to-cart del PDP) y, en éxito, se le pasa la respuesta a su método
 * nativo `renderContents()` — el único lugar del tema que sabe reemplazar
 * #CartDrawer y quitarle la clase `is-empty`. Antes este archivo solo
 * parcheaba a mano el innerHTML de #cart-icon-bubble, por lo que el drawer
 * quedaba con el HTML "vacío" del último render de página hasta refrescar.
 * `renderContents(data, true)` usa el segundo argumento (`skipOpen`, agregado
 * en cart-drawer.js/cart-notification.js sin tocar el comportamiento de sus
 * demás llamadores) para mantener el flujo silencioso de esta sección: solo
 * toast, sin abrir el drawer solo — igual que hoy, pero con el drawer ya
 * sincronizado para cuando el cliente lo abra manualmente.
 *
 * Uso: marca el botón con la clase "js-dcse-quick-add" y uno de estos atributos:
 *   data-variant-id="123456789"             -> variante conocida (más directo)
 *   data-product-handle="mi-producto"       -> se agrega la 1ª variante disponible
 *   data-link="/products/mi-producto"       -> se extrae el handle del enlace
 *   data-quick-add-message="Texto opcional" -> mensaje del toast al agregar
 */
(function () {
  if (window.DCSEQuickAdd) return;

  var DEFAULT_MESSAGE = 'Producto agregado al carrito';
  var DEFAULT_ERROR = 'No se pudo agregar. Intenta de nuevo.';

  function getToast() {
    var toast = document.getElementById('dcse-toast');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.id = 'dcse-toast';
    toast.className = 'dcse-toast';
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('role', 'status');
    toast.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M9 16.17l-3.88-3.88-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>' +
      '<span class="dcse-toast__text"></span>';
    document.body.appendChild(toast);
    return toast;
  }

  function showToast(message) {
    var toast = getToast();
    var text = toast.querySelector('.dcse-toast__text');
    if (text) text.textContent = message || DEFAULT_MESSAGE;
    toast.classList.add('dcse-toast--visible');
    window.clearTimeout(toast._dcseHideTimer);
    toast._dcseHideTimer = window.setTimeout(function () {
      toast.classList.remove('dcse-toast--visible');
    }, 2500);
  }

  function applyCartIconHtml(sectionHtml) {
    if (!sectionHtml) return;
    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var rendered = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
    if (!rendered) return;
    document.querySelectorAll('#cart-icon-bubble').forEach(function (icon) {
      icon.innerHTML = rendered.innerHTML;
    });
  }

  // Respaldo por si la página no tiene <cart-drawer>/<cart-notification> (p. ej. un
  // template que solo trae el ícono del carrito) o si /cart/add.js no devolvió
  // "sections" (una integración externa llamando a updateCartBubble() directamente).
  function updateCartBubble(sectionsFromResponse) {
    var html = sectionsFromResponse && sectionsFromResponse['cart-icon-bubble'];
    if (html) {
      applyCartIconHtml(html);
      return Promise.resolve();
    }

    return fetch(window.location.pathname + '?section_id=cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (response) {
        return response.text();
      })
      .then(applyCartIconHtml)
      .catch(function () {
        /* si falla, simplemente no se actualiza la burbuja; el producto ya quedó en el carrito */
      });
  }

  // <cart-drawer> y <cart-notification> exponen la misma forma (getSectionsToRender
  // / renderContents), igual que asume assets/product-form.js para el add-to-cart
  // nativo del PDP — se resuelve así para funcionar sin cambios si la tienda alguna
  // vez cambia cart_type de "drawer" a "notification" en el editor de temas.
  function getCartRoot() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function resolveVariantId(button) {
    var direct = button.getAttribute('data-variant-id');
    if (direct) return Promise.resolve(parseInt(direct, 10) || null);

    var handle = button.getAttribute('data-product-handle');
    if (!handle) {
      var link = button.getAttribute('data-link') || '';
      var match = link.match(/\/products\/([^/?#]+)/);
      if (match) handle = match[1];
    }
    if (!handle) return Promise.resolve(null);

    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (product) {
        return product && product.variants && product.variants.length ? product.variants[0].id : null;
      })
      .catch(function () {
        return null;
      });
  }

  function handleClick(event) {
    var button = event.target.closest('.js-dcse-quick-add');
    if (!button || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    var cartRoot = getCartRoot();
    var sectionsToRequest =
      cartRoot && typeof cartRoot.getSectionsToRender === 'function'
        ? cartRoot.getSectionsToRender().map(function (section) {
            return section.id;
          })
        : ['cart-icon-bubble'];

    resolveVariantId(button)
      .then(function (variantId) {
        if (!variantId) {
          showToast('No se pudo identificar el producto.');
          return null;
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionsToRequest,
            sections_url: window.location.pathname,
          }),
        }).then(function (response) {
          if (!response.ok) {
            return response.json().then(function (error) {
              throw error;
            });
          }
          return response.json();
        });
      })
      .then(function (data) {
        if (!data) return;
        // skipOpen=true: mismo método nativo que sincroniza #CartDrawer (o la
        // notificación), pero sin abrirlo solo — esta sección se queda con el
        // flujo de solo-toast que ya tenía.
        if (cartRoot && typeof cartRoot.renderContents === 'function') {
          cartRoot.renderContents(data, true);
          // renderContents() solo reemplaza el contenido de #CartDrawer; igual
          // que hace el propio product-form.js del PDP (en su .finally()), hay
          // que quitar "is-empty" del elemento raíz aparte, o el header del
          // drawer se queda oculto (cart-drawer.is-empty .drawer__header {
          // display:none } en component-cart-drawer.css) aunque el ítem ya
          // esté en el carrito.
          if (cartRoot.classList.contains('is-empty')) cartRoot.classList.remove('is-empty');
        } else {
          updateCartBubble(data.sections);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: data }));
        showToast(button.getAttribute('data-quick-add-message') || DEFAULT_MESSAGE);
      })
      .catch(function (error) {
        var message = (error && (error.description || error.message)) || DEFAULT_ERROR;
        showToast(message);
      })
      .finally(function () {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  }

  document.addEventListener('click', handleClick);

  window.DCSEQuickAdd = {
    showToast: showToast,
    updateCartBubble: updateCartBubble,
  };
})();
